/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React from 'react';
import { PriceComponent } from '@msdyn365-commerce/components';
import { Address, CartLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ICoreContext, IImageSettings, Image } from '@msdyn365-commerce/core';
import { IShipMultipleAddressesLineViewModel } from '../../modules/krannich-c-checkout-shipping-address/krannich-c-checkout-shipping-address';
import { ICheckoutShippingCartLineInfo } from '../../modules/krannich-c-checkout-shipping-address/components';

export interface IShipMultipleAddressesLineProps extends CartLine {
    shipMultipleAddressesLine: IShipMultipleAddressesLineViewModel;
    shippingLineImage: ICheckoutShippingCartLineInfo;
    price?: number;
    context: ICoreContext;
    product?: SimpleProduct;
    imageSettings?: IImageSettings;
    quantity: number;
    moduleId: string;
    moduleTypeName: string;
    currencyCode: string | undefined;
    customerAddresses: Address[];
    error?: string;
    isMobile: boolean;
    onSelect(line: IShipMultipleAddressesLineViewModel, isChecked: boolean): void;
    onShippingAddressChange(shippingLine: CartLine, event: React.ChangeEvent<HTMLSelectElement>): void;
    resources: {
        unavailableProductErrorMessage?: string;
        outOfStockProductErrorMessage?: string;
    };
}

export interface IShipMultipleAddressesLineViewProps {
    key?: string;
    productImage?: React.ReactNode;
    productPrice?: React.ReactNode;
    productCurrencyCode?: React.ReactNode;
    productName?: string;
    productNumber?: React.ReactNode;
    productInfo?: React.ReactNode;
    productLabel?: React.ReactNode;
    productUnitOfMeasure?: React.ReactNode;
    productQuantity?: number;
    productListId?: string;
    productAddress?: React.ReactNode;
    selectLine?: React.ReactNode;
    popUp?: JSX.Element;
    productAddressPopup?: JSX.Element | null | undefined;
    isMobile?: boolean;
}

/**
 * On Select line function.
 * @param shipMultipleAddressesLine - Ship multiple addresses line.
 * @param input - Ship multiple addresses line props.
 * @returns Set on select shipping line.
 */
const onSelectLineHandler = (shipMultipleAddressesLine: IShipMultipleAddressesLineViewModel, input: IShipMultipleAddressesLineProps) => (
    event: React.ChangeEvent<HTMLInputElement>
) => {
    shipMultipleAddressesLine.isChecked = !shipMultipleAddressesLine.isChecked;

    input.onSelect(shipMultipleAddressesLine, event.target.checked);
};

export const ShipMultipleAddressesLineView = (input: IShipMultipleAddressesLineProps): IShipMultipleAddressesLineViewProps | null => {
    const { customerAddresses, shipMultipleAddressesLine, shippingLineImage, product, context, onShippingAddressChange } = input;

    if (!product) {
        return null;
    }

    const { ItemId } = product;
    const { Quantity, ShippingAddress } = shipMultipleAddressesLine.cartLine;

    return {
        selectLine: (
            <label className='checkbox-container'>
                <input
                    className='checkbox-input'
                    type='checkbox'
                    checked={shipMultipleAddressesLine.isChecked}
                    aria-checked={shipMultipleAddressesLine.isChecked}
                    onChange={onSelectLineHandler(shipMultipleAddressesLine, input)}
                />
                <span className='checkmark' />
            </label>
        ),
        productImage: renderProductImage(
            '',
            context,
            product.Name!,
            shippingLineImage.imageProps.fallBackSrc,
            shippingLineImage.imageProps.src
        ),
        productInfo: renderProductInfo(input),
        productPrice: renderProductPrice(input),
        productNumber: product && (
            <>
                <div>{ItemId}</div>
                {input.error !== 'NONE' ? <div>{renderProductError(input)}</div> : undefined}
            </>
        ),
        productQuantity: Quantity,
        productAddress: (
            <select
                className={'msc-address-select__current-address-dropdown'}
                onChange={e => onShippingAddressChange(shipMultipleAddressesLine.cartLine, e)}
                value={ShippingAddress?.RecordId}
            >
                {customerAddresses?.map((address: Address, index: number) => (
                    <option
                        key={index}
                        value={address.RecordId}
                        aria-label={address.FullAddress}
                        className={'msc-address-select__current-address-text'}
                    >
                        {address.FullAddress}
                    </option>
                ))}
            </select>
        )
    };
};

const renderProductImage = (
    className: string,
    context: ICoreContext,
    heading: string,
    image?: string,
    fallbackImage?: string
): JSX.Element | null => {
    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=64&h=64&m=6', w: 0, h: 0 },
            lg: { q: 'w=64&h=64&m=6', w: 0, h: 0 },
            xl: { q: 'w=64&h=64&m=6', w: 0, h: 0 }
        },
        lazyload: true
    };

    return (
        <Image
            requestContext={context?.actionContext?.requestContext}
            className={className}
            altText={heading}
            title={heading}
            src={image}
            fallBackSrc={fallbackImage}
            gridSettings={context?.request?.gridSettings!}
            imageSettings={defaultImageSettings}
            loadFailureBehavior='empty'
            tabIndex={0}
        />
    );
};

const renderProductDimensions = (props: IShipMultipleAddressesLineProps): (string | undefined)[] => {
    const { product } = props;
    if (!product || !product.Dimensions) {
        return [];
    }
    return product.Dimensions.map(dimension => {
        if (!dimension.DimensionValue || !dimension.DimensionValue.Value) {
            return undefined;
        }
        return dimension.DimensionValue.Value;
    });
};

const renderProductInfo = (props: IShipMultipleAddressesLineProps): React.ReactNode | undefined => {
    const { product } = props;
    const name = product?.Name;
    const dimensions = renderProductDimensions(props);
    if (!product) {
        return [];
    }
    const productName = <div>{name}</div>;
    const dimensionsLine: string = dimensions.join(', ');
    return (
        <div>
            <div>{productName}</div>
            <div>{dimensionsLine}</div>
        </div>
    );
};

const renderProductPrice = (props: IShipMultipleAddressesLineProps): React.ReactNode | undefined => {
    const { product } = props;

    if (!product?.Price) {
        return null;
    }

    return (
        <span>
            <PriceComponent
                data={{
                    price: {
                        CustomerContextualPrice: product.Price
                    }
                }}
                context={props.context}
                id={props.moduleId}
                typeName={props.moduleTypeName}
                currencyCode={props.currencyCode}
            />
        </span>
    );
};

const renderProductError = (props: IShipMultipleAddressesLineProps) => {
    const { error, resources } = props;

    if (!error) {
        return '';
    }

    const errorMessages = {
        OOS: resources.outOfStockProductErrorMessage,
        UNASSORTED: resources.unavailableProductErrorMessage,
        NONE: ''
    };

    return errorMessages[error];
};
